/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { type TaskOptionsArgs, TaskService } from 'app/common/services';
import { type FileSystem } from 'app/common/types/mo';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ClientCifsShareService {
    constructor(
        private http: HttpService,
        private taskService: TaskService,
    ) { }

    getBatchRest(cifsID: number | string, vstoreId: string): Rest {
        return this.http.makeUrl('CIFSUser.getBatch')
            .setParams({ vstoreId })
            .setFilters([
                { field: 'PARENTID', matchMode: ':', value: cifsID },
            ]);
    }

    countRest(cifsID: number | string, vstoreId: string): Rest {
        return this.http.makeUrl('CIFSUser.count')
            .setParams({ vstoreId })
            .setFilters([
                { field: 'PARENTID', matchMode: ':', value: cifsID },
            ]);
    }

    getBatch(cifsID: number | string, vstoreId: string, range: number[], options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(cifsID, vstoreId).setParams({
            range: '[' + range[0] + '-' + range[1] + ']',
        });
        return this.http.get(rest, options);
    }

    getCount(cifsID: number | string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.countRest(cifsID, vstoreId);
        return this.http.get(rest, options);
    }

    get(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSUser.get').format(id);
        return this.http.get(rest, options);
    }

    add(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSUser.post');
        return this.http.post(rest, params, options);
    }

    addClients(objName: string, params: any, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('cifsClient.add', 'V2');
        return this.taskService.post('addClient2CifsShare', objName, rest, params, options);
    }

    modify(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSUser.put').format(id);
        return this.http.put(rest, params, options);
    }

    remove(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSUser.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    /**
     * 获取对应租户CIFS智能推荐数据
     */
    getSmartguiCIFS(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<any> {
        const rest = this.http.makeUrl('smartgui.getCifsshare', 'V2').format(vstoreId);
        return this.http.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询单个文件系统
     * @param id
     */
    getFileSystem(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<FileSystem> {
        const rest = this.http.makeUrl('fileSystem.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }
}
